import { memo } from 'react';

import { Loader } from '../../designSystem/Loader';
import { cn } from '../../lib/styles';

export interface LoadingOverlayProps {
  /** When true, shows a dimmed overlay with a centered {@link Loader}. */
  visible: boolean;
  /** Content behind the overlay. The wrapper uses `position: relative` so the overlay covers this subtree. */
  children?: React.ReactNode;
  /** Classes for the outer wrapper (use `min-h-*` or `h-full` when the overlay should fill a parent-sized area). */
  className?: string;
  /** Classes for the dimmed layer on top of `children`. */
  overlayClassName?: string;
  /** Props passed to {@link Loader}. */
  loaderProps?: React.ComponentProps<typeof Loader>;
  /** `z-index` of the overlay layer. Defaults to 20. */
  zIndex?: number;
}

const LoadingOverlayBase = ({
  visible,
  children,
  className,
  overlayClassName,
  loaderProps,
  zIndex = 20,
}: LoadingOverlayProps) => {
  if (!visible) {
    if (children == null) return null;

    return children;
  }

  const { className: loaderClassName, ...restLoaderProps } = loaderProps ?? {};

  return (
    <div className={cn('relative', className)}>
      {children}

      <div
        className={cn(
          'absolute inset-0 flex items-center justify-center',
          'bg-bg-sur-primary/80 dark:bg-gray-16/70',
          overlayClassName,
        )}
        style={{ zIndex }}
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-loading-overlay
      >
        <Loader className={cn('size-5', loaderClassName)} {...restLoaderProps} />
      </div>
    </div>
  );
};

export const LoadingOverlay = memo(LoadingOverlayBase);
